/**
 * Perguntas prontas do Vision, keyed no CPF/CNPJ do contato.
 *
 * Uma definição, dois consumidores: o estado vazio do painel
 * (`VisionEmptyState`) e o menu de atalhos do reply box
 * (`AiQuickActionsMenu`). Toda pergunta exige documento — sem ele o agente
 * não tem o que consultar, então quem chama trata a ausência em voz alta em
 * vez de mandar a pergunta cega.
 *
 * Cada pergunta declara em `tools` os VERBOS canônicos de que ela depende. O
 * registry de integrações nomeia toda tool como `<slug>_<verbo>` (slugs: ixc,
 * sgp, hubsoft, voalle, rbx), então o verbo é sempre sufixo do nome real —
 * é isso que permite casar o atalho com a allowlist do papel personalizado
 * (`vision_tools`, nomes completos) sem o frontend conhecer o provedor da
 * conta. Ver `visionPromptsFor`.
 */
export const VISION_QUICK_PROMPTS = Object.freeze([
  {
    key: 'customer_lookup',
    icon: 'i-lucide-user-search',
    label: 'VISION.PROMPTS.CUSTOMER_LOOKUP.LABEL',
    prompt: 'VISION.PROMPTS.CUSTOMER_LOOKUP.CONTENT',
    tools: ['buscar_cliente'],
  },
  {
    key: 'boleto_second_copy',
    icon: 'i-lucide-receipt-text',
    label: 'VISION.PROMPTS.BOLETO_SECOND_COPY.LABEL',
    prompt: 'VISION.PROMPTS.BOLETO_SECOND_COPY.CONTENT',
    // É `listar_titulos` que traz link do boleto e código PIX.
    tools: ['listar_titulos'],
  },
  {
    key: 'connection_status',
    icon: 'i-lucide-wifi',
    label: 'VISION.PROMPTS.CONNECTION_STATUS.LABEL',
    prompt: 'VISION.PROMPTS.CONNECTION_STATUS.CONTENT',
    tools: ['status_conexao'],
  },
]);

// simplificação: casa o verbo por sufixo do nome da tool, que só vale
// enquanto o registry mantiver `<slug>_<verbo>`. Provedor que fuja do
// contrato some com o atalho do agente restrito (administrador não muda).
// Upgrade: o backend mandar, junto da allowlist, o verbo canônico de cada
// tool, em vez de o frontend inferir do nome.
const hasVerb = (allowedTools, verb) =>
  allowedTools.some(name => String(name).endsWith(`_${verb}`));

/**
 * Perguntas prontas que o usuário atual PODE disparar.
 *
 * @param {string[]|null} allowedTools allowlist do `useVisionAccess`: `null`
 *   para administrador (tudo liberado) ou a lista de nomes completos de tool
 *   liberados pelo papel personalizado (`['sgp_buscar_cliente', ...]`).
 * @returns {Array} só os atalhos cujos verbos TODOS aparecem na allowlist.
 *
 * Oferecer um atalho cuja tool o papel não libera é prometer o que o Vision
 * não entrega: o agente clica, o orchestrator não tem a tool e a pergunta
 * falha. Fora da lista, o agente ainda pode perguntar em texto livre.
 */
export const visionPromptsFor = allowedTools => {
  if (allowedTools === null || allowedTools === undefined) {
    return VISION_QUICK_PROMPTS;
  }
  if (!Array.isArray(allowedTools)) return [];

  return VISION_QUICK_PROMPTS.filter(option =>
    option.tools.every(verb => hasVerb(allowedTools, verb))
  );
};

/**
 * Texto final da pergunta (i18n com o documento do contato). Com contrato
 * escolhido na conversa, acrescenta a âncora "do contrato X" — vale pra
 * qualquer provedor (o id é o `selected_id_contrato` genérico do runtime).
 */
export const buildVisionPrompt = (t, option, document, contract = null) => {
  const base = t(option.prompt, { cpf: document });
  if (!contract?.id_contrato) return base;
  return `${base} ${t('VISION.PROMPTS.CONTRACT_SUFFIX', {
    id: contract.id_contrato,
    label: contract.label || '',
  })}`;
};
